import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { motion } from "framer-motion";

// Local cn helper (avoids path resolution issues inside container)
const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

// Defines the variants for the card's color scheme
const folderCardVariants = cva(
  "relative overflow-hidden flex flex-col justify-between rounded-xl border p-4 transition-all duration-200 ease-in-out bg-white text-gray-900 border-black",
  {
    variants: {
      variant: {
        default: "",
        project: "",
        system: "",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

// Defines the props for the FolderCard component
export interface FolderCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof folderCardVariants> {
  /** The icon to be displayed in the card. */
  icon: React.ReactNode;
  /** The title or name of the folder. */
  title: string;
  /** The size of the folder, e.g., "25 MB". */
  size: string;
}

const FolderCard = React.forwardRef<HTMLDivElement, FolderCardProps>(
  ({ className, variant, icon, title, size, ...props }, ref) => {
    // Minimal animation (no hover scaling)
    const cardAnimation = {
      initial: { opacity: 0, y: 10 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.25, ease: "easeOut" },
    };

    return (
      <motion.div
        className={cn(folderCardVariants({ variant }), className)}
        ref={ref}
        {...cardAnimation}
        {...(props as any)} // allow native div props alongside framer-motion
      >
        {/* Icon container */}
        <div className="mb-6">
          {icon}
        </div>
        
        {/* Text content container */}
        <div className="flex flex-col">
          <h3 className="text-sm font-semibold text-gray-900 tracking-tight">
            {title}
          </h3>
          <p className="text-xs text-gray-600">
            {size}
          </p>
        </div>
      </motion.div>
    );
  }
);
FolderCard.displayName = "FolderCard";

export { FolderCard };
